import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faCheck, faSignOutAlt, faChevronLeft, faChevronRight, faInfoCircle, faEdit, faEnvelopeOpenText, faTimes, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const StaffAttendance = ({ showAlert }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [user, setUser] = useState(null);

  // Leave Modal State
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveStatus, setLeaveStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Leave History State
  const [showLeavesHistoryModal, setShowLeavesHistoryModal] = useState(false);
  const [leavesHistory, setLeavesHistory] = useState([]);

  const leaveOptions = [
    { label: "Present", value: "present" },
    { label: "Paid Leave", value: "paid_leave" },
    { label: "Half Paid Leave", value: "half_paid_leave" },
    { label: "Unpaid Leave", value: "unpaid_leave" },
    { label: "Unpaid Half Day", value: "unpaid_half_day" }
  ];

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    setUser(userDetails);
    fetchAttendance();
  }, [currentDate]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userDetails = JSON.parse(localStorage.getItem('userDetails'));
      if (!userDetails?._id) return;

      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();

      const res = await axios.post(buildApiUrl("hr/attendance/staff"),
        { staffId: userDetails._id, month, year },
        { headers: { 'auth-token': token } }
      );

      setAttendanceData(res.data.attendance || []);
      
      // Also fetch user profile for latest leaveBalance
      const userRes = await axios.get(buildApiUrl("users/getuser"), {
        headers: { 'auth-token': token }
      });
      if (userRes.data) setUser(userRes.data);

      const todayStr = new Date().toLocaleDateString('en-CA');
      const todayRec = (res.data.attendance || []).find(a => {
        const d = new Date(a.date);
        return d.toLocaleDateString('en-CA') === todayStr;
      });
      setTodayRecord(todayRec);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (!leaveStatus || !fromDate || !toDate) {
      if (showAlert) showAlert("Please fill all required fields", "warning");
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const restaurantId = localStorage.getItem('currentRestaurantId');
      
      // Calculate dates between from and to
      const start = new Date(fromDate);
      const end = new Date(toDate);
      const dateArray = [];
      let current = new Date(start);

      while (current <= end) {
        dateArray.push(new Date(current).toLocaleDateString('en-CA'));
        current.setDate(current.getDate() + 1);
      }

      // Map UI values to Model values
      let status = 'leave';
      let type = 'unpaid';

      if (leaveStatus === 'paid_leave') { status = 'leave'; type = 'paid'; }
      else if (leaveStatus === 'half_paid_leave') { status = 'half-day'; type = 'paid'; }
      else if (leaveStatus === 'unpaid_half_day') { status = 'half-day'; type = 'unpaid'; }
      else if (leaveStatus === 'present') { status = 'present'; type = 'none'; }

      // Send requests for each date (Simple approach for now)
      for (const d of dateArray) {
        await axios.post(buildApiUrl("hr/attendance/leave/request"),
          { restaurantId, date: d, reason: remarks, status, leaveType: type },
          { headers: { 'auth-token': token } }
        );
      }

      if (showAlert) showAlert("Leave requested successfully!", "success");
      setShowLeaveModal(false);
      fetchAttendance();
      // Reset form
      setLeaveStatus('');
      setFromDate('');
      setToDate('');
      setRemarks('');
    } catch (error) {
      console.error("Error requesting leave:", error);
      if (showAlert) showAlert("Failed to request leave", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  const fetchLeavesHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userDetails = JSON.parse(localStorage.getItem('userDetails'));
      
      const res = await axios.post(buildApiUrl("hr/attendance/staff"),
        { staffId: userDetails._id },
        { headers: { 'auth-token': token } }
      );

      // Filter for leaves only (exclude normal working days like present/late)
      const leaves = (res.data.attendance || []).filter(a => 
        (a.status === 'leave' || a.status === 'half-day' || a.leaveStatus || a.leaveReason) && 
        a.status !== 'present' && a.status !== 'late'
      );
      
      setLeavesHistory(leaves);
      setShowLeavesHistoryModal(true);
    } catch (error) {
      console.error("Error fetching leave history:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn) return null;
    const start = new Date(checkIn);
    const end = checkOut ? new Date(checkOut) : new Date();
    const diffMs = end - start;
    
    if (diffMs < 0) return "0m";
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleSelfAttendance = async (type) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const restaurantId = localStorage.getItem('currentRestaurantId');
      
      const endpoint = type === 'in' ? 'checkin' : 'checkout';
      const res = await axios.post(buildApiUrl(`hr/attendance/${endpoint}`),
        { restaurantId },
        { headers: { 'auth-token': token } }
      );

      if (res.data.success) {
        setTodayRecord(res.data.attendance);
        if (showAlert) showAlert(type === 'in' ? "Checked In!" : "Checked Out!", "success");
      }
      fetchAttendance();
    } catch (error) {
       if (showAlert) showAlert(error.response?.data?.message || "Action failed", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  // Calendar Logic
  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const renderCalendar = () => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const totalDays = daysInMonth(month, year);
    const startDay = firstDayOfMonth(month, year);
    const days = [];

    // Fill empty slots
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ height: '60px' }}></div>);
    }

    // Fill actual days
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const record = attendanceData.find(a => new Date(a.date).toLocaleDateString('en-CA') === dateStr);
      const isToday = new Date().toLocaleDateString('en-CA') === dateStr;

      days.push(
        <div key={d} style={{ 
          height: '60px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderRadius: '8px',
          border: isToday ? '1px solid #3b82f6' : '1px solid transparent',
          background: record ? (record.status === 'present' ? '#22c55e10' : record.status === 'leave' ? '#f59e0b10' : '#ef444410') : 'transparent',
          position: 'relative'
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: isToday ? 'bold' : 'normal', color: isToday ? '#3b82f6' : 'white' }}>{d}</span>
          {record && (
            <div style={{ 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%', 
              background: record.status === 'present' ? '#22c55e' : record.status === 'leave' ? '#f59e0b' : '#ef4444',
              marginTop: '4px'
            }}></div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0c', color: 'white' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        
        {/* TOP HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1c1c1f', padding: '1.5rem 2rem', borderRadius: '16px', marginBottom: '2rem', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
              Mark attendance for today ({new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })})
            </h2>
            <p style={{ margin: '0.3rem 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>You can mark your attendance for today.</p>
          </div>

          {todayRecord?.status === 'leave' ? (
            <div style={{ padding: '0.8rem 1.5rem', background: '#f59e0b20', color: '#f59e0b', borderRadius: '8px', fontWeight: 'bold' }}>
              On Leave Today 🗓️
            </div>
          ) : !todayRecord?.checkIn ? (
            <button 
              onClick={() => handleSelfAttendance('in')}
              disabled={actionLoading}
              style={{ padding: '0.8rem 1.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <FontAwesomeIcon icon={faCheck} /> Check In
            </button>
          ) : (
            <button 
              onClick={() => handleSelfAttendance('out')}
              disabled={actionLoading}
              style={{ 
                padding: '0.8rem 1.5rem', 
                background: '#f59e0b', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                fontWeight: 'bold', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem' 
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} /> {todayRecord?.checkOut ? 'Update Check Out' : 'Check Out'}
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' }}>
          
          {/* MAIN COLUMN (Calendar & Table) */}
          <div>
            {/* Calendar Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}><FontAwesomeIcon icon={faChevronLeft} /></button>
                <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}><FontAwesomeIcon icon={faChevronRight} /></button>
                <select 
                  value={currentDate.getMonth()} 
                  onChange={(e) => setCurrentDate(new Date(currentDate.getFullYear(), parseInt(e.target.value), 1))}
                  style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 'bold' }}
                >
                  {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
                </select>
                <select 
                  value={currentDate.getFullYear()} 
                  onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), currentDate.getMonth(), 1))}
                  style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '6px', color: 'white', fontWeight: 'bold' }}
                >
                  {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <button 
                onClick={() => setCurrentDate(new Date())}
                style={{ padding: '0.5rem 1rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', cursor: 'pointer', fontWeight: '600' }}
              >
                Today
              </button>
            </div>

            {/* Calendar Grid */}
            <div style={{ background: '#1c1c1f', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '2rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', textAlign: 'center', marginBottom: '1rem', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', fontWeight: 'bold' }}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day}>{day}</div>)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
                {renderCalendar()}
              </div>
            </div>

            <button 
                onClick={() => setShowLeaveModal(true)}
                style={{ padding: '0.8rem 1.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '2rem' }}
            >
                Apply Leave
            </button>

            {/* Attendance Log Table */}
            <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0' }}>Attendance</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>To update your attendance data, please click on the edit button next to each date.</p>
            </div>
            
            <div style={{ background: '#1c1c1f', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.3)' }}>
                        <tr>
                            <th style={{ padding: '1rem' }}>Date</th>
                            <th style={{ padding: '1rem' }}>Status</th>
                            <th style={{ padding: '1rem' }}>Check In</th>
                            <th style={{ padding: '1rem' }}>Check Out</th>
                            <th style={{ padding: '1rem' }}>Duration</th>
                            <th style={{ padding: '1rem' }}>Remarks</th>
                            <th style={{ padding: '1rem' }}>Edit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.sort((a,b) => new Date(b.date) - new Date(a.date)).map(record => {
                             const duration = calculateDuration(record?.checkIn, record?.checkOut);
                             return (
                                <tr key={record._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                    <td style={{ padding: '1rem' }}>{new Date(record.date).toLocaleDateString('en-GB')}</td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{ 
                                            padding: '0.2rem 0.5rem', 
                                            borderRadius: '4px', 
                                            background: record.status === 'present' ? '#22c55e20' : '#ef444420',
                                            color: record.status === 'present' ? '#22c55e' : '#ef4444',
                                            fontSize: '0.7rem',
                                            fontWeight: '700',
                                            textTransform: 'uppercase'
                                        }}>{record.status}</span>
                                    </td>
                                    <td style={{ padding: '1rem' }}>{record.checkIn ? new Date(record.checkIn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</td>
                                    <td style={{ padding: '1rem' }}>{record.checkOut ? new Date(record.checkOut).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}</td>
                                    <td style={{ padding: '1rem' }}>{duration || '-'}</td>
                                    <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.4)' }}>{record.notes || '-'}</td>
                                    <td style={{ padding: '1rem' }}><FontAwesomeIcon icon={faEdit} style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.2)' }} /></td>
                                </tr>
                             )
                        })}
                    </tbody>
                </table>
            </div>
          </div>

          {/* SIDEBAR COLUMN */}
          <div>
            <div style={{ background: '#1c1c1f', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', position: 'sticky', top: '2rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>Your leave balance</h3>
                <div style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.2rem' }}>Paid Leave Balance</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{user?.leaveBalance || 0} Days</div>
                </div>
                <button 
                  onClick={fetchLeavesHistory}
                  style={{ width: '100%', padding: '0.8rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.08)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                >
                    View leaves taken
                </button>
            </div>
          </div>

        </div>
      </div>

      {/* APPLY FOR LEAVE MODAL */}
      {showLeaveModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#1c1c1f', width: '450px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            
            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Apply for leave</h3>
              <FontAwesomeIcon 
                icon={faTimes} 
                style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.5)' }} 
                onClick={() => setShowLeaveModal(false)}
              />
            </div>

            <form onSubmit={handleSubmitLeave} style={{ padding: '1.5rem' }}>
              
              <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>Status</label>
                <div 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  style={{ 
                    padding: '0.8rem 1rem', 
                    background: '#232326', 
                    border: isDropdownOpen ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: leaveStatus ? 'white' : 'rgba(255,255,255,0.2)'
                  }}
                >
                  <span>{leaveOptions.find(o => o.value === leaveStatus)?.label || 'Status'}</span>
                  <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.8rem' }} />
                </div>

                {isDropdownOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#232326', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                    {leaveOptions.map(opt => (
                      <div 
                        key={opt.value}
                        onClick={() => {
                          setLeaveStatus(opt.value);
                          setIsDropdownOpen(false);
                        }}
                        style={{ 
                          padding: '1rem', 
                          cursor: 'pointer', 
                          fontSize: '0.9rem',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          transition: 'background 0.2s'
                        }}
                        className="leave-opt"
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 20px 1fr', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>From</label>
                  <input 
                    type="date" 
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{ width: '100%', padding: '0.8rem', background: '#232326', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                  />
                </div>
                <div style={{ textAlign: 'center', marginTop: '1.5rem', color: 'rgba(255,255,255,0.3)' }}>→</div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>to</label>
                  <input 
                    type="date" 
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{ width: '100%', padding: '0.8rem', background: '#232326', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>Remarks (optional)</label>
                <textarea 
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Reason for leave..."
                  style={{ width: '100%', height: '100px', padding: '1rem', background: '#232326', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white', resize: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  type="submit"
                  disabled={actionLoading}
                  style={{ flex: 1, padding: '0.85rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {actionLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowLeaveModal(false)}
                  style={{ flex: 1, padding: '0.85rem', background: '#2d2d30', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
      {/* LEAVE HISTORY MODAL */}
      {showLeavesHistoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#1c1c1f', width: '680px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
            
            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>Your Leave History</h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>Track all your past and pending leave requests.</p>
              </div>
              <FontAwesomeIcon 
                icon={faTimes} 
                style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem' }} 
                onClick={() => setShowLeavesHistoryModal(false)}
              />
            </div>

            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {leavesHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'rgba(255,255,255,0.2)' }}>
                  <FontAwesomeIcon icon={faEnvelopeOpenText} style={{ fontSize: '3rem', marginBottom: '1.5rem', opacity: 0.5 }} />
                  <h3 style={{ margin: 0 }}>No leave history yet</h3>
                  <p style={{ marginTop: '0.5rem' }}>Your approved and pending leaves will appear here.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {leavesHistory.sort((a,b) => new Date(b.date) - new Date(a.date)).map(leave => (
                    <div key={leave._id} style={{ background: 'rgba(255,255,255,0.02)', padding: '1.2rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.03)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center' }}>
                        <div style={{ textAlign: 'center', minWidth: '60px', padding: '0.5rem', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{new Date(leave.date).toLocaleString('default', { month: 'short' })}</div>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{new Date(leave.date).getDate()}</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '0.95rem', marginBottom: '0.2rem' }}>
                            <span style={{ textTransform: 'capitalize' }}>
                                {(leave.leaveType === 'none' ? 'General' : leave.leaveType || 'Unpaid')} Leave 
                                {leave.status === 'half-day' ? ' (Half Day)' : ''}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                            "{leave.leaveReason || 'No reason provided'}"
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ 
                          padding: '0.4rem 0.8rem', 
                          borderRadius: '6px', 
                          fontSize: '0.75rem', 
                          fontWeight: '800', 
                          textTransform: 'uppercase',
                          background: leave.leaveStatus === 'approved' ? '#22c55e20' : leave.leaveStatus === 'rejected' ? '#ef444420' : '#f59e0b20',
                          color: leave.leaveStatus === 'approved' ? '#22c55e' : leave.leaveStatus === 'rejected' ? '#ef4444' : '#f59e0b',
                          letterSpacing: '0.4px'
                        }}>
                          {leave.leaveStatus || 'Pending'}
                        </span>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.6rem' }}>
                          Applied on {new Date(leave.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ padding: '1.2rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'right', background: 'rgba(0,0,0,0.1)' }}>
              <button 
                onClick={() => setShowLeavesHistoryModal(false)}
                style={{ padding: '0.8rem 2.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffAttendance;
